import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type Region = "uk-eu" | "us-eu" | "global";

export interface ResearchStat {
  id: string;
  stat_key: string;
  value_display: string;
  value_numeric: number | null;
  headline: string;
  description: string;
  audience: string;
  theme: string;
  region: Region;
  pillar_tags: string[];
  industry_tags: string[];
  source_label: string;
  source_url: string | null;
  source_year: number | null;
  published: boolean;
  sort_order: number;
}

export interface StatsFilter {
  theme?: string | string[];
  audience?: string | string[];
  pillarId?: string;
  industryId?: string;
  limit?: number;
}

/* ── lightweight client-side region detection (cached) ── */
const REGION_KEY = "tango_visitor_region";
let cachedRegion: Region | null = null;

async function detectRegion(): Promise<Region> {
  if (cachedRegion) return cachedRegion;
  try {
    const stored = typeof window !== "undefined" ? localStorage.getItem(REGION_KEY) : null;
    if (stored === "uk-eu" || stored === "us-eu" || stored === "global") {
      cachedRegion = stored;
      return stored;
    }
  } catch {}

  let region: Region = "global";
  try {
    // Locale heuristic first (zero-cost)
    const lang = typeof navigator !== "undefined" ? navigator.language || "" : "";
    if (/^en-US|^es-US/i.test(lang)) region = "us-eu";
    else if (/^en-GB|^en-IE|^fr|^de|^es|^it|^nl|^pt/i.test(lang)) region = "uk-eu";

    // Refine with IP lookup (best-effort, short timeout)
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 1500);
    const res = await fetch("https://ipapi.co/json/", { signal: ctrl.signal });
    clearTimeout(t);
    if (res.ok) {
      const j = await res.json();
      const code = String(j?.country_code || "").toUpperCase();
      if (["US", "CA", "MX"].includes(code)) region = "us-eu";
      else if (code) region = "uk-eu";
    }
  } catch {}

  cachedRegion = region;
  try { localStorage.setItem(REGION_KEY, region); } catch {}
  return region;
}

function matchesFilter(stat: ResearchStat, f: StatsFilter): boolean {
  if (f.theme) {
    const themes = Array.isArray(f.theme) ? f.theme : [f.theme];
    if (!themes.includes(stat.theme)) return false;
  }
  if (f.audience) {
    const auds = Array.isArray(f.audience) ? f.audience : [f.audience];
    if (!auds.includes(stat.audience)) return false;
  }
  if (f.pillarId && !stat.pillar_tags.includes(f.pillarId)) return false;
  if (f.industryId && !stat.industry_tags.includes(f.industryId)) return false;
  return true;
}

/**
 * Fetches published research stats with regional auto-resolution.
 * Order of preference per stat_key: visitor region → global → first available.
 */
export function useResearchStats(filter: StatsFilter = {}) {
  return useQuery({
    queryKey: ["research-stats", filter],
    queryFn: async () => {
      const region = await detectRegion();

      const { data, error } = await supabase
        .from("research_stats")
        .select("*")
        .eq("published", true)
        .order("sort_order");
      if (error) throw error;

      const all = (data || []) as ResearchStat[];
      const filtered = all.filter((s) => matchesFilter(s, filter));

      // Group by stat_key root (strip trailing -uk/-us if present so variants collapse)
      const groups = new Map<string, ResearchStat[]>();
      for (const s of filtered) {
        const root = s.stat_key.replace(/-(uk|us|global)$/i, "");
        const arr = groups.get(root) || [];
        arr.push(s);
        groups.set(root, arr);
      }

      const resolved: ResearchStat[] = [];
      for (const variants of groups.values()) {
        const exact = variants.find((v) => v.region === region);
        const global = variants.find((v) => v.region === "global");
        resolved.push(exact || global || variants[0]);
      }

      resolved.sort((a, b) => a.sort_order - b.sort_order);
      return filter.limit ? resolved.slice(0, filter.limit) : resolved;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export interface EmployeeArchetype {
  id: string;
  archetype_key: string;
  label: string;
  one_liner: string;
  description: string;
  colour_hex: string;
  icon_name: string;
  pillar_tags: string[];
  published: boolean;
  sort_order: number;
}

export function useEmployeeArchetypes() {
  return useQuery({
    queryKey: ["employee-archetypes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employee_archetypes")
        .select("*")
        .eq("published", true)
        .order("sort_order");
      if (error) throw error;
      return (data || []) as EmployeeArchetype[];
    },
    staleTime: 5 * 60 * 1000,
  });
}